import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";

import { initGA, trackPageView } from "./config/analytics";

// ── Route-based code splitting ─────────────────────────────────
// Each page becomes its own JS chunk, downloaded on demand.
// Drops first-paint payload from a single 320 KB bundle to a small
// app shell + the chunk for the page the user actually opened.
const Home          = lazy(() => import("./pages/Home/Home"));
const MoviesList    = lazy(() => import("./pages/MovieList/MovieList"));
const TvShowList    = lazy(() => import("./pages/TvShowList/TvShowList"));
const MovieDetail   = lazy(() => import("./pages/MovieDetail/MovieDetail"));
const TvShowDetail  = lazy(() => import("./pages/TvShowDetail/TvShowDetail"));
const Search        = lazy(() => import("./pages/Search/Search"));
const Contact       = lazy(() => import("./pages/Contact/Contact"));
const FilterPage    = lazy(() => import("./pages/Filter/FilterPage"));
const NotFound      = lazy(() => import("./pages/NotFound/NotFound"));
const Feedback      = lazy(() => import("./pages/StaticPages/Feedback"));
const Community     = lazy(() => import("./pages/StaticPages/Community"));
const Privacy       = lazy(() => import("./pages/StaticPages/Privacy"));
const DMCA          = lazy(() => import("./pages/StaticPages/DMCA"));

function Analytics() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

// Lightweight fallback while a route chunk loads.
// Keep it minimal so it doesn't itself slow the first paint.
function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} aria-busy="true" />;
}

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}>
      <Analytics />
      <Navbar />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/movies/page/:page" element={<MoviesList />} />
          <Route path="/tv-shows/page/:page" element={<TvShowList />} />
          <Route path="/movie/:slug" element={<MovieDetail />} />
          <Route path="/tv-show/:slug" element={<TvShowDetail />} />
          <Route path="/search/:keyword" element={<Search />} />
          <Route path="/search/:keyword/page/:page" element={<Search />} />
          <Route path="/contact" element={<Contact />} />

          {/* Movies filters */}
          <Route path="/movies/genre/:id/:name/page/:page" element={<FilterPage />} />
          <Route path="/movies/genre/:id/:name" element={<FilterPage />} />
          <Route path="/movies/tag/:id/:name/page/:page" element={<FilterPage />} />
          <Route path="/movies/tag/:id/:name" element={<FilterPage />} />
          <Route path="/movies/year/:name/page/:page" element={<FilterPage />} />
          <Route path="/movies/year/:name" element={<FilterPage />} />

          {/* TV Shows filters */}
          <Route path="/tv-shows/genre/:id/:name/page/:page" element={<FilterPage />} />
          <Route path="/tv-shows/genre/:id/:name" element={<FilterPage />} />
          <Route path="/tv-shows/tag/:id/:name/page/:page" element={<FilterPage />} />
          <Route path="/tv-shows/tag/:id/:name" element={<FilterPage />} />
          <Route path="/tv-shows/year/:name/page/:page" element={<FilterPage />} />
          <Route path="/tv-shows/year/:name" element={<FilterPage />} />

          <Route path="/feedback" element={<Feedback />} />
          <Route path="/community" element={<Community />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/dmca" element={<DMCA />} />

          {/* ✅ 404 — must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </BrowserRouter>
  );
}

export default App;
